/**
 * /userreport-portal：与 UserReport 流程一致；校验为身份证号（identification_no），
 * 列表接口使用病区返回的 UserCode 作为 query_no 请求 /api/reglist/stream-page。
 */
import React, { useState, useEffect, useCallback, useRef, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, message } from 'antd';
import styles from './styles/userReport.module.less';
import { useAppConfig } from '@state';
import { utils } from '@ohif/core';
import ValidateModal from './ValidateModal';
import {
  normalizeDataIndex,
  formatStudyDate,
  getStateText,
} from '../../../utils/reportHelper';

const { apiService } = utils;

const PORTAL_LAST_ID_SUFFIX_KEY = 'userreport_portal_last_id_suffix';
const PORTAL_QUERY_NO_PREFIX = 'userreport_portal_query_no_';

function clearPortalPersistedKeys() {
  const last = String(localStorage.getItem(PORTAL_LAST_ID_SUFFIX_KEY) ?? '').trim();
  if (last) {
    localStorage.removeItem(`${PORTAL_QUERY_NO_PREFIX}${last}`);
    localStorage.removeItem(`isValidate_${last}`);
  }
  localStorage.removeItem(PORTAL_LAST_ID_SUFFIX_KEY);
}

interface StudyData {
  register_key?: string;
  yearMonthDay?: string;
  bodypart_name?: string;
  modality_code?: string;
  chinese_name?: string;
  sex?: string;
  age?: string;
  study_time?: string;
  check_view2?: string;
  diagnosis_result2?: string;
  state?: string;
  stateText?: string;
  index?: number;
}

const UserReportPortal = React.memo(
  () => {
    const [appConfig] = useAppConfig();
    const navigate = useNavigate();
    const location = useLocation();
    const [studies, setStudies] = useState<StudyData[]>([]);
    const [listLoading, setListLoading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();
    const [form] = Form.useForm();
    const tokenManager = (window as any).tokenManager;

    const [validatedIdSuffix, setValidatedIdSuffix] = useState('');

    const listLoadingRef = useRef(listLoading);
    useEffect(() => {
      listLoadingRef.current = listLoading;
    }, [listLoading]);

    const stripNavStateOnceRef = useRef(false);

    const skipPortalAuth = useMemo(
      () =>
        (location.state as { skipPortalAuth?: boolean } | null)?.skipPortalAuth === true,
      [location.state]
    );

    const [authState, setAuthState] = useState({
      isValidate: false,
      modalVisible: true,
      isSubmitting: false,
    });

    /** 每次进入门户需重新验证；从胶片详情点「历史检查」返回时带 state，保留本会话验证结果 */
    useLayoutEffect(() => {
      if (stripNavStateOnceRef.current) {
        stripNavStateOnceRef.current = false;
        return;
      }

      if (skipPortalAuth) {
        const s = String(localStorage.getItem(PORTAL_LAST_ID_SUFFIX_KEY) ?? '').trim().toUpperCase();
        const sk = s ? `isValidate_${s}` : '';
        const hasLs = sk ? !!localStorage.getItem(sk) : false;
        const tokenOk = !!tokenManager.getCurrentToken();
        const ok = hasLs && tokenOk;
        setValidatedIdSuffix(s);
        setAuthState({
          isValidate: ok,
          modalVisible: !ok,
          isSubmitting: false,
        });
        stripNavStateOnceRef.current = true;
        navigate(
          { pathname: location.pathname, search: location.search },
          { replace: true, state: {} }
        );
        return;
      }

      clearPortalPersistedKeys();
      if (tokenManager?.clearToken) {
        tokenManager.clearToken();
      }
      setValidatedIdSuffix('');
      setStudies([]);
      setAuthState({
        isValidate: false,
        modalVisible: true,
        isSubmitting: false,
      });
    }, [location.pathname, skipPortalAuth, navigate, tokenManager]);

    const getDataByQueryNo = useCallback(
      async (queryNo: string) => {
        if (listLoadingRef.current) return;
        if (!queryNo) return;
        const formData = new FormData();
        formData.append('query_no', queryNo);
        formData.append('cloud_image', '1');
        formData.append('pageSize', '100');
        setListLoading(true);

        try {
          const res = await apiService({
            uri: '/api/reglist/stream-page',
            method: 'POST',
            data: formData,
            type: null,
          });

          if (!res?.Data) throw new Error('检查列表数据为空');

          let data = res.Data.map((item: any, index: number) => ({ ...item, index: index + 1 }));
          let normalizedData = normalizeDataIndex(data, true);

          normalizedData.sort((a, b) => {
            const parseDate = (dateString: string) => {
              if (!dateString) return -Infinity;
              const date = new Date(dateString);
              return isNaN(date.getTime()) ? -Infinity : date.getTime();
            };
            return parseDate(b.study_time) - parseDate(a.study_time);
          });

          normalizedData = normalizedData.map((item: any) => ({
            ...item,
            yearMonthDay: item.study_time ? formatStudyDate(item.study_time).yearMonthDay : '',
            stateText: item.state ? getStateText(item.state) : '',
          }));

          setStudies(normalizedData);
        } catch (error) {
          setStudies([]);
          messageApi.error(
            `获取检查列表失败: ${error instanceof Error ? error.message : '未知错误'}`
          );
        } finally {
          setListLoading(false);
        }
      },
      [messageApi]
    );

    useEffect(() => {
      console.log('tokenManager.getCurrentToken', tokenManager.getCurrentToken());
      if (
        authState.isValidate &&
        !listLoadingRef.current &&
        tokenManager.getCurrentToken() &&
        validatedIdSuffix
      ) {
        const queryNo = String(
          localStorage.getItem(`${PORTAL_QUERY_NO_PREFIX}${validatedIdSuffix}`) ?? ''
        ).trim();
        if (queryNo) {
          void getDataByQueryNo(queryNo);
        }
      }
    }, [
      authState.isValidate,
      validatedIdSuffix,
      location.search,
      getDataByQueryNo,
      authState.modalVisible,
    ]);

    useEffect(() => {
      const onUnauthorized = () => {
        setStudies([]);
        setAuthState(prev => ({
          ...prev,
          isValidate: false,
          modalVisible: true,
          isSubmitting: false,
        }));
      };
      window.addEventListener('token:unauthorized', onUnauthorized as EventListener);
      return () => {
        window.removeEventListener('token:unauthorized', onUnauthorized as EventListener);
      };
    }, []);

    const handleOK = useCallback(async () => {
      if (authState.isSubmitting) return;
      setAuthState(prev => ({ ...prev, isSubmitting: true }));

      try {
        const values = await form.validateFields();
        const identificationNo = String(values.idCard ?? '')
          .trim()
          .toUpperCase();
        const formData = new FormData();
        formData.append('identification_no', identificationNo);

        const queryParams = new URLSearchParams(location.search);
        for (const [key, value] of queryParams.entries()) {
          formData.append(key, value);
        }

        const res = await apiService({
          uri: '/api/login/CloudVerify',
          method: 'POST',
          data: formData,
          type: null,
        });

        if (res.Status === '0' || res.Status === 0) {
          const queryNo = String(res?.UserCode ?? '').trim();
          if (!queryNo) {
            setAuthState(prev => ({ ...prev, isSubmitting: false }));
            setTimeout(
              () => messageApi.error('验证成功但未返回查询号，请联系管理员'),
              0
            );
            return;
          }

          if (tokenManager && tokenManager.setToken) {
            tokenManager.setToken(res.Token, res.ExpiresUtc, res.RefreshToken);
          }

          const sk = `isValidate_${identificationNo}`;
          localStorage.setItem(sk, 'true');
          localStorage.setItem(PORTAL_LAST_ID_SUFFIX_KEY, identificationNo);
          localStorage.setItem(`${PORTAL_QUERY_NO_PREFIX}${identificationNo}`, queryNo);

          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('isValidate_') && key !== sk) {
              localStorage.removeItem(key);
            }
          });

          setValidatedIdSuffix(identificationNo);
          setAuthState({
            isValidate: true,
            modalVisible: false,
            isSubmitting: false,
          });
          messageApi.success('身份验证成功');
        } else if (res.Status === '1' || res.Status === 1) {
          setAuthState(prev => ({ ...prev, isSubmitting: false }));
          setTimeout(() => messageApi.error('验证失败，请检查身份证号'), 0);
        } else {
          setAuthState(prev => ({ ...prev, isSubmitting: false }));
          setTimeout(() => messageApi.error('未知的验证响应，请重试'), 0);
        }
      } catch (error) {
        setAuthState(prev => ({ ...prev, isSubmitting: false }));
        setTimeout(() => {
          messageApi.error(error instanceof Error ? error.message : '验证过程出错，请重试');
        }, 0);
      }
    }, [form, messageApi, location.search, authState.isSubmitting, tokenManager]);

    const handleShowDetail = useCallback(
      (item: StudyData) => {
        try {
          if (!item.register_key) {
            messageApi.error('缺少唯一标识，无法查看详情');
            return;
          }
          const currentSearchParams = new URLSearchParams(location.search);
          currentSearchParams.set('register_key', item.register_key);
          currentSearchParams.set('from', 'userreportportal');
          currentSearchParams.set('id_suffix', validatedIdSuffix);
          navigate(`/reportdetail?${currentSearchParams.toString()}`);
        } catch (error) {
          messageApi.error('跳转失败，请重试');
        }
      },
      [messageApi, location.search, navigate, validatedIdSuffix]
    );

    return (
      <>
        {contextHolder}
        <div
          className={styles.container}
          id="userreport-portal"
        >
          <h3 className={styles.title}>我的云胶片</h3>
          <div className={styles.content}>
            <div className={styles.header}>
              <h3 className={styles.sectionTitle}>云胶片</h3>
              <p>如果您已经做了检查且报告已出，但页面无数据，请联系工作人员。</p>
            </div>
            <div className={styles.listWrapper}>
              {listLoading ? (
                <p>加载中...</p>
              ) : studies.length > 0 ? (
                <ul className={styles.reportList}>
                  {studies.map((item, index) => (
                    <li
                      key={item.register_key || index}
                      className={styles.reportItem}
                      onClick={() => handleShowDetail(item)}
                    >
                      <h3 className={styles.itemDate}>{item.yearMonthDay}</h3>
                      <div className={styles.itemDetails}>
                        <h3 className={styles.itemTitle}>{item.modality_code}</h3>
                        <hr className={styles.divider} />
                        <h3 className={styles.itemTitle}>{item.bodypart_name}</h3>
                        <hr className={styles.divider} />
                        <p className={styles.hospital}>{appConfig.hospitalName}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>暂无数据</p>
              )}
            </div>
          </div>
          <ValidateModal
            visible={authState.modalVisible}
            isSubmitting={authState.isSubmitting}
            onOk={handleOK}
            form={form}
            verificationMode="idCardFull"
            title="查询检查记录"
          />
        </div>
      </>
    );
  },
  () => true
);

UserReportPortal.displayName = 'UserReportPortal';

export default UserReportPortal;
